// Admin Payout Queue — list view.
//
// Operators land here to process pending withdrawal requests. Summary stats
// at the top (pending / processing / completed-this-month) regardless of
// the current filter. Filters narrow the table below. Each row links to
// the detail screen where actions happen.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface PayoutRow {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  payout_rail: string;
  status: string;
  external_reference: string | null;
  failure_reason: string | null;
  requested_at: string;
  approved_at: string | null;
  processed_at: string | null;
  completed_at: string | null;
  rail_details: {
    account_holder_name?: string;
    details?: Record<string, unknown>;
  } | null;
  profiles: {
    id: string;
    username: string;
    avatar_url: string | null;
    country_code: string | null;
    platform_status: string;
  } | null;
}

interface Stats {
  requested: { count: number; sum: number };
  processing: { count: number; sum: number };
  completed_this_month: { count: number; sum: number };
}

interface ListResponse {
  payouts: PayoutRow[];
  total: number;
  stats: Stats;
}

const STATUS_OPTIONS = [
  { value: "queue", label: "Queue (requested + processing)" },
  { value: "requested", label: "Requested only" },
  { value: "processing", label: "Processing only" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "all", label: "All" },
];

const RAIL_LABELS: Record<string, string> = {
  bank: "Bank",
  mobile_money: "Mobile money",
  other: "Other",
};

function statusColor(status: string): string {
  switch (status) {
    case "completed":
      return "var(--upvote, #22C55E)";
    case "failed":
    case "cancelled":
      return "var(--danger, #ef4444)";
    case "processing":
      return "var(--accent)";
    default:
      return "var(--text-secondary)";
  }
}

function formatUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function shortMethod(row: PayoutRow): string {
  const rail = row.payout_rail;
  const details = row.rail_details?.details ?? {};
  if (rail === "bank") {
    const bank = (details.bank_name as string | undefined) ?? "";
    const acct = ((details.account_number as string | undefined) ?? "").slice(
      -4,
    );
    return [bank, acct ? `••${acct}` : ""].filter(Boolean).join(" · ");
  }
  if (rail === "mobile_money") {
    const provider = (details.provider as string | undefined) ?? "";
    const phone = (details.phone_number as string | undefined) ?? "";
    return [provider, phone].filter(Boolean).join(" · ");
  }
  return (details.description as string | undefined) ?? "";
}

export default function AdminPayoutsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>("queue");
  const [railFilter, setRailFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search to avoid spamming the API on each keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter === "queue") {
        params.set("status", "requested,processing");
      } else if (statusFilter !== "all") {
        params.set("status", statusFilter);
      }
      if (railFilter) params.set("rail", railFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("limit", "100");
      const res = await api<ListResponse>(`/admin/payouts?${params}`);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, railFilter, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const rows = data?.payouts ?? [];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Payout Queue</h1>
        <CardSubtle>
          Process creator withdrawal requests. Oldest pending first.
        </CardSubtle>
      </div>

      {/* Stats row */}
      <div style={styles.statsRow}>
        <StatCard
          label="Pending review"
          count={data?.stats.requested.count}
          sum={data?.stats.requested.sum}
        />
        <StatCard
          label="Processing"
          count={data?.stats.processing.count}
          sum={data?.stats.processing.sum}
        />
        <StatCard
          label="Completed this month"
          count={data?.stats.completed_this_month.count}
          sum={data?.stats.completed_this_month.sum}
        />
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <div style={styles.filterRow}>
          <div style={{ flex: 1 }}>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <Label>Rail</Label>
            <Select
              value={railFilter}
              onChange={(e) => setRailFilter(e.target.value)}
            >
              <option value="">All rails</option>
              <option value="bank">Bank</option>
              <option value="mobile_money">Mobile money</option>
              <option value="other">Other</option>
            </Select>
          </div>
          <div style={{ flex: 2 }}>
            <Label>Search creator</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="username substring"
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      {loading ? (
        <Card>
          <div style={styles.loadingWrap}>
            <Spinner />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div style={styles.errorBar}>{error}</div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardTitle>No payouts</CardTitle>
          <CardSubtle>
            Nothing matches the current filters. Try widening the search.
          </CardSubtle>
        </Card>
      ) : (
        <Card padding={0}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                <Th>Creator</Th>
                <Th>Amount</Th>
                <Th>Method</Th>
                <Th>Country</Th>
                <Th>Status</Th>
                <Th>Requested</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  style={styles.tableRow}
                  onClick={() => navigate(`/admin/payouts/${row.id}`)}
                >
                  <Td>
                    <div style={styles.creatorCell}>
                      {row.profiles?.avatar_url ? (
                        <img
                          src={row.profiles.avatar_url}
                          alt={row.profiles.username}
                          style={styles.avatar}
                        />
                      ) : (
                        <div style={styles.avatarPlaceholder} />
                      )}
                      <span>
                        @{row.profiles?.username ?? "(deleted)"}
                        {row.profiles?.platform_status === "banned" && (
                          <span style={styles.bannedTag}>banned</span>
                        )}
                        {row.profiles?.platform_status === "under_review" && (
                          <span style={styles.reviewTag}>under review</span>
                        )}
                      </span>
                    </div>
                  </Td>
                  <Td>
                    <strong>{formatUsd(row.amount)}</strong>
                  </Td>
                  <Td>
                    <div>{RAIL_LABELS[row.payout_rail] ?? row.payout_rail}</div>
                    <CardSubtle style={{ fontSize: 12 }}>
                      {shortMethod(row)}
                    </CardSubtle>
                  </Td>
                  <Td>{row.profiles?.country_code ?? "—"}</Td>
                  <Td>
                    <span style={{ color: statusColor(row.status) }}>
                      {row.status}
                    </span>
                  </Td>
                  <Td>
                    <CardSubtle style={{ fontSize: 12 }}>
                      {formatDate(row.requested_at)}
                    </CardSubtle>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function StatCard({
  label,
  count,
  sum,
}: {
  label: string;
  count?: number;
  sum?: number;
}) {
  return (
    <Card style={{ flex: 1 }}>
      <CardSubtle>{label}</CardSubtle>
      <div style={styles.statValue}>{count ?? "—"}</div>
      <CardSubtle>{sum != null ? formatUsd(sum) : ""}</CardSubtle>
    </Card>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}

function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  h1: { margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" },
  statsRow: { display: "flex", gap: 12, marginBottom: 16 },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: "4px 0",
  },
  filterRow: { display: "flex", gap: 12 },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  tableHead: {
    background: "rgba(255,255,255,0.02)",
    textAlign: "left",
  },
  tableRow: {
    cursor: "pointer",
    borderTop: "1px solid var(--border)",
  },
  th: {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  td: {
    padding: "12px",
    verticalAlign: "top",
    color: "var(--text-primary)",
  },
  creatorCell: { display: "flex", alignItems: "center", gap: 8 },
  avatar: { width: 24, height: 24, borderRadius: 12, objectFit: "cover" },
  avatarPlaceholder: {
    width: 24,
    height: 24,
    borderRadius: 12,
    background: "var(--border)",
  },
  bannedTag: {
    marginLeft: 8,
    fontSize: 11,
    color: "var(--danger, #ef4444)",
    fontWeight: 600,
  },
  reviewTag: {
    marginLeft: 8,
    fontSize: 11,
    color: "var(--accent)",
    fontWeight: 600,
  },
};
